import { defineStore } from "pinia";
import { ElNotification, ElMessageBox } from "element-plus";
import { UserStatus } from "~/interfaces/user";
import { ApiKey } from "~/common/API/baseApi.js";
import AuthService, { type BindMessenger } from "~/services/AuthService";

//  https://api.stage-data-light.socialjet.pro/user/telegram/bot/check-auth?authKey=Data-Light-b600d5907a79a4c46e633fd245e8720817304579007185

const timeoutValueToCheckTg = 60000;
const intervalValueToCheckTg = 3000;

export const useUserStore = defineStore("user", () => {
  // ВАЖНО: НЕ вызываем useRuntimeConfig() здесь - это вызовет ошибку на SSR
  // Вызываем его lazy внутри функций где нужен

  const id: Ref<string> = ref("");
  const name: Ref<string> = ref("");
  let status = UserStatus.REGISTERED;
  const telegram: Ref<string> = ref("");
  const email: Ref<string> = ref("");
  const emailConfirmed: Ref<boolean> = ref(false);
  const authKey: Ref<string> = ref("");
  const authorized: Ref<string> = ref("");
  const telegramId: Ref<string> = ref("");
  // Через какого бота идёт вход: у мессенджеров свои ручки check-auth
  const authMessenger: Ref<BindMessenger> = ref("telegram");
  const user: Ref<any> = ref(null);
  const intervalIdToTgCheck: Ref<number> = ref(0);
  const timeoutIdToTgCheck: Ref<number> = ref(0);

  const loginDialogVisible: Ref<boolean> = ref(false);
  const isLoadingTelegramAuthorize: Ref<boolean> = ref(false);
  const isOpenMenu: Ref<boolean> = ref(false);

  // Взял из стока, зачем пока не понятно
  function setStatus(data: UserStatus) {
    status = data;
  }

  function setLS() {
    if (!import.meta.client) return;
    localStorage.setItem("useUserStore__authKey", authKey.value);
    localStorage.setItem("useUserStore__authorized", authorized.value);
    localStorage.setItem("useUserStore__id", id.value);
  } // setLS

  // 1: запросить host
  // stage-data-light.socialjet.pro
  // /user/telegram/bot/generate-auth-key
  function resetAuthKey() {
    authKey.value = "";
    authorized.value = "";
    id.value = "";
    setLS();
  } // resetAuthKey

  // Токен перестал действовать: забываем вход, иначе шапка так и покажет меню
  // вошедшего пользователя. authKey не трогаем — им может идти новый вход через бота
  function dropSession() {
    authorized.value = "";
    id.value = "";
    user.value = null;
    ApiKey.set("");
    setLS();
  } // dropSession

  function setAuthorized(key: string, auth: string, idV: string) {
    authKey.value = key;
    authorized.value = auth;
    id.value = idV;
    setLS();
  } // setAuthorized

  function requestAuthKey() {
    if (!import.meta.client) return;
    const useUserStoreAuthorized = localStorage.getItem(
      "useUserStore__authorized"
    );
    if (useUserStoreAuthorized) authorized.value = useUserStoreAuthorized;

    const useUserStoreAuthKey = localStorage.getItem("useUserStore__authKey");
    if (useUserStoreAuthKey) authKey.value = useUserStoreAuthKey;

    // id без токена — остаток протухшего входа, по нему шапка показала бы меню
    const useUserStoreId = localStorage.getItem("useUserStore__id");
    if (useUserStoreId && authorized.value) id.value = useUserStoreId;

    if (authorized.value) return;
    if (authKey.value && authKey.value !== "undefined") return;

    $fetch("/user/telegram/bot/generate-auth-key", {
      baseURL: useRuntimeConfig().public.LIGHT_API_ORIGIN,
      method: "POST",
      onResponse({ response }) {
        authKey.value = response._data.authKey;
        localStorage.setItem("useUserStore__authKey", authKey.value);
      },
    });
  } // requestAuthKey

  function checkTelegramAuthorize() {
    if (!import.meta.client) return;
    // new check
    clearInterval(intervalIdToTgCheck.value);
    clearTimeout(timeoutIdToTgCheck.value);

    timeoutIdToTgCheck.value = window.setTimeout(() => {
      clearInterval(intervalIdToTgCheck.value);
    }, timeoutValueToCheckTg);

    intervalIdToTgCheck.value = window.setInterval(() => {
      if (authorized.value && id.value) {
        clearInterval(intervalIdToTgCheck.value);
      }
      useFetch(`/user/${authMessenger.value}/bot/check-auth`, {
        baseURL: useRuntimeConfig().public.LIGHT_API_ORIGIN,
        method: "GET",
        query: {
          authKey: authKey.value,
        },
        onResponse({ response }) {
          // { id: number; accessToken: string }
          if (response._data.accessToken && response._data.id) {
            authorized.value = response._data.accessToken;
            ApiKey.set(response._data.accessToken);
            useCookie("authorizedP").value = response._data.accessToken;
            id.value = response._data.id;
            isLoadingTelegramAuthorize.value = false;
            loginDialogVisible.value = false;
            ElNotification({
              title: `Успешная авторизация через ${authMessenger.value === "max" ? "MAX" : "Telegram"}`,
              message: "Спасибо!",
              type: "success",
            });
            setLS();
            window.location.reload();
            clearInterval(intervalIdToTgCheck.value);
            clearTimeout(timeoutIdToTgCheck.value);
          } else {
            ElNotification({
              title: "Ожидание авторизации",
              message: "Ждём подтверждения от бота...",
              type: "warning",
            });
          }
        },
      });
    }, intervalValueToCheckTg);
  } // checkTelegramAuthorize

  async function emailLogin(emailValue: string, password: string) {
    if (!import.meta.client) return;
    try {
      const { id: userId, accessToken } = await AuthService.loginEmail(emailValue, password);
      ApiKey.set(accessToken);
      useCookie("authorizedP").value = accessToken;
      setAuthorized('', accessToken, String(userId));
      loginDialogVisible.value = false;
      window.location.reload();
    } catch (error) {
      throw error;
    }
  }

  function stopTelegramAuthorize() {
    if (!import.meta.client) return;
    clearInterval(intervalIdToTgCheck.value);
    clearTimeout(timeoutIdToTgCheck.value);
    isLoadingTelegramAuthorize.value = false;
  }

  // Вход через бота: берём ключ, уводим пользователя в бота и ждём результат
  // поллингом. Ручки и ссылки у мессенджеров свои.
  function startMessengerAuthorize(messenger: BindMessenger = "telegram") {
    // Бот MAX — обычная веб-страница, и в текущей вкладке она затёрла бы
    // кабинет. Открываем вкладку сразу по клику: сделать это после ответа
    // сервера нельзя — браузер сочтёт её всплывающим окном и заблокирует.
    // Телеграм оставляем как был: tg:// уводит в приложение, а не на страницу.
    const botWindow = messenger === "max" ? window.open("", "_blank") : null;

    AuthService.generateAuthKey(messenger).then((res) => {
      authMessenger.value = messenger;
      authKey.value = res;

      const config = useRuntimeConfig().public;
      const prefix =
        messenger === "max"
          ? config.RESOLVE_MAX_AUTHORIZE_PREFIX
          : config.RESOLVE_TELEGRAM_AUTHORIZE_PREFIX;
      const url = `${prefix}${authKey.value}`;

      if (botWindow) botWindow.location.href = url;
      else window.location.href = url;

      isLoadingTelegramAuthorize.value = true;
      checkTelegramAuthorize();
    });
  }

  function startTelegramAuthorize() {
    startMessengerAuthorize("telegram");
  } // startTelegramAuthorize

  return {
    id,
    name,
    status,
    telegram,
    email,
    emailConfirmed,
    user,
    authKey,
    authorized,
    loginDialogVisible,
    intervalIdToTgCheck,
    timeoutIdToTgCheck,
    telegramId,
    isLoadingTelegramAuthorize,
    emailLogin,
    setLS,
    requestAuthKey,
    resetAuthKey,
    dropSession,
    setAuthorized,
    startTelegramAuthorize,
    startMessengerAuthorize,
    stopTelegramAuthorize,
    checkTelegramAuthorize,
    setStatus,
    isOpenMenu,
  };
});
